"use client";

import type { CSSProperties } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight, Phone, Clock } from "lucide-react";
import { fadeUp, staggerContainer } from "@/lib/animations";
import { useLang } from "@/lib/LanguageContext";

export type CTAVariant = "home" | "services" | "service" | "work" | "clients" | "faq";

/** Per-page visual: the image, where to crop it on the mobile banner, and the glow tint (r,g,b). */
const VISUALS: Record<CTAVariant, { src: string; position: string; glow: string }> = {
  home: { src: "/background-images/rule-your-brand.webp?v=3", position: "center 65%", glow: "224,64,160" },
  services: { src: "/background-images/tool-stack.webp?v=3", position: "center 45%", glow: "155,89,245" },
  service: { src: "/background-images/workflow.webp?v=3", position: "center 55%", glow: "224,64,160" },
  work: { src: "/background-images/workflow2.webp?v=3", position: "center 50%", glow: "255,149,60" },
  clients: { src: "/background-images/floating-bubble.webp?v=3", position: "center 50%", glow: "155,89,245" },
  faq: { src: "/background-images/communication.webp?v=3", position: "center 50%", glow: "224,64,160" },
};

interface CTABannerProps {
  /** Which page the banner sits on — picks the copy and the image */
  variant?: CTAVariant;
  /** Show the "or call us" link (hidden on the FAQ page by default) */
  showCall?: boolean;
}

export default function CTABanner({ variant = "home", showCall = variant !== "faq" }: CTABannerProps) {
  const { t } = useLang();
  const copy = t.ctaBanner.variants[variant];
  const visual = VISUALS[variant];
  const glow = (a: number) => `rgba(${visual.glow},${a})`;

  return (
    <section className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      {/* Ambient glows, tinted per page */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute left-1/4 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: `radial-gradient(circle, ${glow(1)}, transparent 70%)`, filter: "blur(100px)" }}
        />
        <div
          className="absolute right-1/4 top-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, #FFB76C, transparent 70%)", filter: "blur(100px)" }}
        />
      </div>

      <div className="container relative z-10">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          {/* Gradient hairline frame */}
          <motion.div
            variants={fadeUp}
            className="rounded-[29px] p-px"
            style={{
              background: `linear-gradient(140deg, ${glow(0.55)} 0%, rgba(255,255,255,0.07) 30%, rgba(255,255,255,0.04) 70%, rgba(255,183,108,0.4) 100%)`,
              boxShadow: `0 40px 100px -40px ${glow(0.45)}, 0 28px 70px rgba(0,0,0,0.6)`,
            }}
          >
            <div
              className="group relative rounded-[28px] overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(20,11,32,0.98) 0%, rgba(11,7,18,1) 100%)" }}
            >
              {/* Fine dot grain */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.035]"
                style={{
                  backgroundImage: "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
                  backgroundSize: "22px 22px",
                }}
              />

              <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] items-center">
                {/* Left — text */}
                <motion.div
                  variants={staggerContainer}
                  className="relative z-20 -mt-16 px-5 pb-8 sm:px-10 sm:pb-10 lg:mt-0 lg:px-10 lg:py-9 xl:px-12 xl:py-10 flex flex-col justify-center"
                >
                  {/* Soft corner glow */}
                  <div
                    aria-hidden
                    className="absolute -top-24 -left-24 h-80 w-80 rounded-full pointer-events-none hidden lg:block"
                    style={{ background: `radial-gradient(circle, ${glow(0.16)}, transparent 65%)`, filter: "blur(40px)" }}
                  />

                  {/* Label badge */}
                  <motion.div variants={fadeUp} className="relative">
                    <span className="inline-flex items-center gap-2.5 rounded-full border border-white/[0.1] bg-[rgba(12,8,20,0.6)] py-1.5 pl-2.5 pr-3.5 backdrop-blur-md">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E040A0] opacity-60" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E040A0]" />
                      </span>
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-white/75">
                        {copy.label}
                      </span>
                    </span>
                  </motion.div>

                  {/* Headline */}
                  <motion.h2
                    variants={fadeUp}
                    className="relative mt-5 font-display text-[34px] sm:text-[2.6rem] xl:text-5xl leading-[1.06] tracking-tight text-white"
                  >
                    {copy.title} <span className="text-gradient-warm">{copy.titleAccent}</span>
                  </motion.h2>

                  <motion.p
                    variants={fadeUp}
                    className="relative mt-4 max-w-md text-[15px] sm:text-base leading-relaxed text-white/55"
                  >
                    {copy.sub}
                  </motion.p>

                  {/* Actions */}
                  <motion.div
                    variants={fadeUp}
                    className="relative mt-6 sm:mt-7 flex flex-col items-stretch sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4"
                  >
                    <Link
                      href="/contact"
                      className="group/btn relative inline-flex items-center justify-between sm:justify-start gap-3 overflow-hidden rounded-full py-2 pl-6 pr-2 text-sm sm:text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        background: "linear-gradient(135deg, #E040A0 0%, #FFB76C 100%)",
                        boxShadow: "0 14px 40px -12px rgba(224,64,160,0.65)",
                      }}
                    >
                      <span aria-hidden className="absolute inset-0 bg-black/15" />
                      {/* Sheen sweep on hover */}
                      <span
                        aria-hidden
                        className="absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-0 transition-transform duration-700 ease-out group-hover/btn:translate-x-[400%]"
                      />
                      <span className="relative whitespace-nowrap">{copy.button}</span>
                      <span className="relative flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-white text-[#0a0a0f] transition-transform duration-300 group-hover/btn:rotate-45">
                        <ArrowUpRight size={18} />
                      </span>
                    </Link>
                    {showCall && (
                      <a
                        href="tel:+359886662020"
                        className="inline-flex items-center justify-center sm:justify-start gap-2.5 rounded-full border border-white/[0.1] bg-white/[0.02] py-2 pl-2 pr-5 text-sm text-white/70 transition-colors duration-300 hover:border-white/25 hover:text-white"
                      >
                        <span className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-white/[0.06]">
                          <Phone size={15} />
                        </span>
                        <span className="whitespace-nowrap">{t.clients.ctaCall.replace(/\s*→\s*$/, "")}</span>
                      </a>
                    )}
                  </motion.div>

                  {/* Reply promise */}
                  <motion.p
                    variants={fadeUp}
                    className="relative mt-3 inline-flex items-center justify-center sm:justify-start gap-1.5 text-xs text-white/40"
                  >
                    <Clock size={12} className="text-[#FFB76C]/80" />
                    {t.ctaBanner.replyPromise}
                  </motion.p>

                  {/* Stats strip */}
                  <motion.div
                    variants={fadeUp}
                    className="relative mt-6 sm:mt-7 grid grid-cols-3 divide-x divide-white/[0.07] border-t border-white/[0.07] pt-6 sm:pt-0 sm:overflow-hidden sm:rounded-2xl sm:border sm:bg-white/[0.02] sm:backdrop-blur-sm"
                  >
                    {t.clients.stats.map((stat, i) => (
                      <div
                        key={stat.label}
                        className="flex flex-col items-center text-center gap-1.5 px-1 sm:items-start sm:text-left sm:px-5 sm:py-4"
                      >
                        <span
                          className={`font-display text-[26px] sm:text-2xl leading-none tracking-tight ${
                            i === 0 ? "text-gradient-warm" : "text-white"
                          }`}
                        >
                          {stat.value}
                        </span>
                        <span className="text-[9px] sm:text-[10.5px] uppercase tracking-[0.08em] sm:tracking-[0.16em] text-white/40 leading-snug">
                          {stat.label}
                        </span>
                      </div>
                    ))}
                  </motion.div>
                </motion.div>

                {/* Right — image panel (top banner on mobile). Masked rather than
                   overlaid so it fades into the card without a colour seam. */}
                <div className="relative order-first lg:order-none h-56 sm:h-72 lg:h-auto lg:min-h-[400px] self-stretch overflow-hidden [mask-image:linear-gradient(180deg,#000_40%,transparent)] lg:[mask-image:linear-gradient(90deg,transparent,#000_45%)]">
                  {/* Tinted glow behind the visual */}
                  <div
                    aria-hidden
                    className="absolute left-1/2 top-1/2 h-3/4 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${glow(0.35)}, transparent 65%)`, filter: "blur(50px)" }}
                  />
                  <div className="cta-float absolute inset-0">
                    <Image
                      src={visual.src}
                      alt=""
                      aria-hidden
                      fill
                      sizes="(min-width: 1024px) 45vw, 100vw"
                      className="object-cover [object-position:var(--pos)] lg:object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                      style={{ "--pos": visual.position, filter: "brightness(0.9) saturate(1.1)" } as CSSProperties}
                    />
                  </div>
                  {/* Desktop — soften the top and bottom edges */}
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none z-10 hidden lg:block"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(11,7,18,0.7) 0%, transparent 20%, transparent 80%, rgba(11,7,18,0.7) 100%)",
                    }}
                  />
                </div>
              </div>

              {/* Top accent hairline */}
              <div
                aria-hidden
                className="absolute top-0 left-0 right-0 h-px pointer-events-none z-30"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,183,108,0.55) 18%, rgba(224,64,160,0.45) 55%, transparent 100%)",
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
